import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AnimatedNumber } from "./AnimatedNumber";

/**
 * Tokenomics — interactive orbital allocation visualization.
 *
 * Allocations come straight from FanzToken.sol:
 *   30% Marketing | 20% Product | 18% Liquidity | 10% Airdrops |
 *    7% Community |  5% Staking |  5% Treasury  |  5% Creator Rewards
 *
 * Each allocation is a sized "planet" floating in space. Hovering /
 * clicking surfaces vesting + purpose. The whole layout uses CSS
 * transforms (no Three.js cost) — feels 3D via parallax, scale, blur,
 * and conic gradients.
 */

type Bucket = {
  key: string;
  label: string;
  pct: number;
  amount: string;
  color: [string, string]; // gradient
  desc: string;
  vesting: string;
};

const BUCKETS: Bucket[] = [
  {
    key: "marketing",
    label: "Marketing",
    pct: 30,
    amount: "300,000,000",
    color: ["#c084fc", "#7c3aed"],
    desc: "Creator acquisition, paid social, partnerships across the 25-platform FANZ network. Largest bucket because growth is the moat.",
    vesting: "Linear over 24 months from TGE",
  },
  {
    key: "product",
    label: "Product",
    pct: 20,
    amount: "200,000,000",
    color: ["#ff2d92", "#a8186b"],
    desc: "Engineering, design, AI tooling, and the FanzCard universal product. Funds the build pipeline.",
    vesting: "Linear over 36 months from TGE",
  },
  {
    key: "liquidity",
    label: "Liquidity",
    pct: 18,
    amount: "180,000,000",
    color: ["#00f2ff", "#0085a3"],
    desc: "DEX liquidity on Base + paired CEX listings. Locked LP via Unicrypt for 24 months.",
    vesting: "Locked LP — no unlock",
  },
  {
    key: "airdrops",
    label: "Airdrops",
    pct: 10,
    amount: "100,000,000",
    color: ["#f5c451", "#a07a18"],
    desc: "Loyalty rewards for top creators and active fans across BoyFanz, GirlFanz, GayFanz and niche platforms.",
    vesting: "5 quarterly drops post-TGE",
  },
  {
    key: "community",
    label: "Community",
    pct: 7,
    amount: "70,000,000",
    color: ["#a855f7", "#5b21b6"],
    desc: "Governance grants, hackathon prizes, content bounties for the FANZ DAO.",
    vesting: "Unlocked, gated by DAO vote",
  },
  {
    key: "staking",
    label: "Staking Rewards",
    pct: 5,
    amount: "50,000,000",
    color: ["#22c55e", "#0e7c3a"],
    desc: "Funds the 20% APY pool. Rewards stream from this allocation continuously.",
    vesting: "Streamed via FanzStaking contract",
  },
  {
    key: "treasury",
    label: "Treasury",
    pct: 5,
    amount: "50,000,000",
    color: ["#60a5fa", "#1e40af"],
    desc: "Operational runway, audits, legal, processor float. Multisig 3-of-5.",
    vesting: "Linear over 48 months from TGE",
  },
  {
    key: "creator",
    label: "Creator Rewards",
    pct: 5,
    amount: "50,000,000",
    color: ["#ec4899", "#9d174d"],
    desc: "Per-platform creator-of-the-month grants. Zero-creator-fee law applies.",
    vesting: "Monthly drip via FanzMoney",
  },
];

export default function Tokenomics() {
  const [active, setActive] = useState<Bucket>(BUCKETS[0]);
  const [layout] = useState<"orbit" | "bars">("orbit");

  return (
    <section id="tokenomics" className="relative mx-auto max-w-7xl px-4 py-20 md:px-8 md:py-28">
      <SectionHeader
        eyebrow="Tokenomics"
        title="One billion FANZ. Distributed with intent."
        sub="Eight buckets. Multi-year vesting. Multisig-locked treasury. Every allocation
             traces to a real on-chain account from FanzToken.sol — no mystery wallets."
      />

      <div className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-[1.1fr_1fr]">
        {/* LEFT — orbital bubbles */}
        <div className="panel relative aspect-square overflow-hidden p-6">
          <div className="pointer-events-none absolute inset-0 opacity-40"
            style={{
              backgroundImage:
                "repeating-radial-gradient(circle at center, rgba(192,132,252,0.05) 0, rgba(192,132,252,0.05) 1px, transparent 1px, transparent 8%)",
            }}
          />

          {/* center hub */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
            <div className="grid h-32 w-32 place-items-center rounded-full border border-white/10 bg-black/60 backdrop-blur-xl">
              <div className="text-center">
                <div className="font-display text-4xl text-white">1B</div>
                <div className="text-[10px] uppercase tracking-widest text-gray-300">FANZ</div>
              </div>
            </div>
          </div>

          {/* bubbles */}
          {layout === "orbit" &&
            BUCKETS.map((b, i) => {
              const total = BUCKETS.length;
              const angle = (i / total) * Math.PI * 2 - Math.PI / 2;
              const radius = 36; // % of container
              const x = 50 + Math.cos(angle) * radius;
              const y = 50 + Math.sin(angle) * radius;
              const size = 36 + b.pct * 1.6; // 41px..96px range
              const isActive = active.key === b.key;

              return (
                <motion.button
                  key={b.key}
                  onClick={() => setActive(b)}
                  onHoverStart={() => setActive(b)}
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  whileHover={{ scale: 1.12 }}
                  viewport={{ once: true }}
                  transition={{
                    delay: 0.06 * i,
                    duration: 0.7,
                    type: "spring",
                    stiffness: 120,
                    damping: 18,
                  }}
                  className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full"
                  style={{
                    left: `${x}%`,
                    top: `${y}%`,
                    width: size,
                    height: size,
                    background: `radial-gradient(circle at 30% 30%, ${b.color[0]} 0%, ${b.color[1]} 75%)`,
                    boxShadow: isActive
                      ? `0 0 0 2px rgba(255,255,255,0.4), 0 0 40px ${b.color[0]}, inset 0 1px 0 rgba(255,255,255,0.4)`
                      : `0 0 24px ${b.color[1]}88, inset 0 1px 0 rgba(255,255,255,0.3)`,
                  }}
                  aria-label={`${b.label} allocation: ${b.pct}%`}
                >
                  <div className="grid h-full w-full place-items-center">
                    <span className="font-display text-lg leading-none text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
                      {b.pct}%
                    </span>
                  </div>
                  {/* connecting line to center */}
                  <div
                    className="pointer-events-none absolute left-1/2 top-1/2 origin-left -translate-y-1/2 opacity-30"
                    style={{
                      width: `${radius}%`,
                      height: 1,
                      background: `linear-gradient(90deg, ${b.color[0]} 0%, transparent 100%)`,
                      transform: `translateY(-50%) rotate(${(angle * 180) / Math.PI + 180}deg)`,
                      transformOrigin: "0% 50%",
                    }}
                  />
                </motion.button>
              );
            })}

          {/* corner labels */}
          <div className="pointer-events-none absolute left-4 top-4 text-[10px] uppercase tracking-widest text-gray-300">
            ON-CHAIN · ERC-20 + Permit + Votes
          </div>
          <div className="pointer-events-none absolute bottom-4 right-4 text-[10px] uppercase tracking-widest text-gray-300">
            Source: FanzToken.sol
          </div>
        </div>

        {/* RIGHT — detail card + table */}
        <div className="flex flex-col gap-4">
          <AnimatePresence mode="wait">
            <motion.div
              key={active.key}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="panel panel-hover relative p-6"
              style={{
                background: `linear-gradient(180deg, ${active.color[0]}18 0%, transparent 60%)`,
              }}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-gray-300">
                    Allocation
                  </div>
                  <h3 className="mt-1 font-display text-4xl text-white">
                    {active.label}
                  </h3>
                </div>
                <div className="flex flex-col items-end">
                  <div className="font-display text-5xl leading-none text-iridescent">
                    {active.pct}%
                  </div>
                  <div className="mt-1 font-mono text-sm text-gray-300">
                    <AnimatedNumber value={Number(active.amount.replace(/,/g, ""))} /> FANZ
                  </div>
                </div>
              </div>

              <p className="mt-5 text-base leading-relaxed text-gray-300">
                {active.desc}
              </p>

              <div className="mt-5 flex items-start gap-3 rounded-xl border border-white/10 bg-black/40 p-4">
                <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg"
                  style={{ background: `linear-gradient(135deg, ${active.color[0]}, ${active.color[1]})` }}>
                  <svg className="h-5 w-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" />
                    <path d="M3 10h18M8 4v4m8-4v4" />
                  </svg>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-gray-300">Vesting</div>
                  <div className="mt-0.5 text-sm font-medium text-white">{active.vesting}</div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* allocation table */}
          <div className="panel divide-y divide-white/[0.05] overflow-hidden">
            {BUCKETS.map((b) => (
              <button
                key={b.key}
                onClick={() => setActive(b)}
                className={`flex w-full items-center gap-4 px-5 py-3 text-left transition-colors ${
                  active.key === b.key ? "bg-white/[0.04]" : "hover:bg-white/[0.02]"
                }`}
              >
                <div
                  className="h-3 w-3 shrink-0 rounded-full"
                  style={{
                    background: `linear-gradient(135deg, ${b.color[0]}, ${b.color[1]})`,
                    boxShadow: `0 0 12px ${b.color[0]}88`,
                  }}
                />
                <span className="flex-1 text-sm font-medium text-white">{b.label}</span>
                <span className="font-mono text-xs text-gray-300">{b.amount}</span>
                <span className="w-14 text-right font-display text-lg text-white">{b.pct}%</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function SectionHeader({
  eyebrow,
  title,
  sub,
}: {
  eyebrow: string;
  title: string;
  sub?: string;
}) {
  return (
    <div className="max-w-3xl">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.6 }}
        className="text-[10px] uppercase tracking-[0.32em] text-accent"
      >
        {eyebrow}
      </motion.div>
      <motion.h2
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.7, delay: 0.1 }}
        className="mt-3 font-display text-4xl tracking-tight text-white md:text-6xl"
      >
        {title}
      </motion.h2>
      {sub && (
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="mt-4 max-w-2xl text-base leading-relaxed text-gray-300 md:text-lg"
        >
          {sub}
        </motion.p>
      )}
    </div>
  );
}
